import React, { useState, useEffect, useRef, useMemo } from "react";
import editIcon from "assets/themeIcons/edit-icon.svg";
import { useAppServices } from "hook/services";
import toast from "react-hot-toast";
import { uploadImageToGhl } from "hook/services";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import GhlMediaGallery from "components/GhlMediaGallery";
import { FaImages } from "react-icons/fa";
import { arrayMove } from "@dnd-kit/sortable";
import { sortBlogsJobsNewsForAdmin } from "utils/submissionDisplayOrder";
import SubmissionSortableProvider from "./SubmissionSortableProvider";
import SubmissionSortableTableRow from "./SubmissionSortableTableRow";
import { useOptimisticSubmissionOrder } from "./useOptimisticSubmissionOrder";
import PrimaryButton from "../../components/PrimaryButton";

/* ---------------- Helpers ---------------- */

const getPlainText = (html) => {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};

const trimText = (html, length = 30) => {
  const text = getPlainText(html);
  if (text.length <= length) return text;
  return text.substring(0, length) + "...";
};

/* ---------------- Component ---------------- */

const Blogs = ({ data, onLoad, agency }) => {
  const currentItems = data || [];
  const AppService = useAppServices();
  const sortedItems = useMemo(
    () => sortBlogsJobsNewsForAdmin(currentItems),
    [currentItems]
  );
  const { rowsToShow, setOptimisticRows, clearOptimisticRows } =
    useOptimisticSubmissionOrder(sortedItems);
  const sortableIds = useMemo(
    () => rowsToShow.map((i) => i._id).filter(Boolean),
    [rowsToShow]
  );

  const [selectedBlog, setSelectedBlog] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ghlGalleryOpen, setGhlGalleryOpen] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [reordering, setReordering] = useState(false);

  const quillRef = useRef(null);
  const quillInstance = useRef(null);

  /* ---------------- Modal ---------------- */

  const handleOpenModal = (blog) => {
    setSelectedBlog(blog);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedBlog(null);
    setIsModalOpen(false);
  };

  const persistDisplayOrder = async (ordered) => {
    setReordering(true);
    try {
      await Promise.all(
        ordered.map((item, i) =>
          AppService.blogs.Update({
            payload: { _id: item._id, display_order: i },
          })
        )
      );
      toast.success("Display order updated");
      onLoad();
    } catch (err) {
      toast.error("Could not update display order");
      throw err;
    } finally {
      setReordering(false);
    }
  };

  const handleDragReorder = async (activeId, overId) => {
    const oldIndex = rowsToShow.findIndex((i) => String(i._id) === activeId);
    const newIndex = rowsToShow.findIndex((i) => String(i._id) === overId);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(rowsToShow, oldIndex, newIndex);
    setOptimisticRows(next);
    try {
      await persistDisplayOrder(next);
    } catch {
      clearOptimisticRows();
    }
  };

  /* ---------------- Image Upload ---------------- */

  const handleGhlImageSelect = (pick) => {
    if (!pick?.url) return;
    setSelectedBlog({ ...selectedBlog, image: pick?.url, image_id: pick?._id });
    toast.success("Image selected.");
  };

  /* ---------------- Save ---------------- */

  const handleSave = async () => {
    try {
      setLoading(true);

      const descriptionHTML =
        quillInstance.current?.root?.innerHTML || selectedBlog?.description;

      const payload = {
        _id: selectedBlog?._id,
        title: selectedBlog?.title,
        description: descriptionHTML,
        image: selectedBlog?.image || "",
        image_id: selectedBlog?.image_id || "",
      };

      await AppService.blogs.Update({ payload });

      toast.success("Blog updated successfully!");
      handleCloseModal();
      onLoad();
    } catch (err) {
      toast.error("Error updating blog");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- Delete ---------------- */

  const handleDelete = async () => {
    try {
      setLoading(true);

      await AppService.blogs.Delete({ 
        query: `_id=${selectedBlog?._id}`,
      });

      toast.success("Blog deleted successfully!");
      setConfirmDelete(false);
      handleCloseModal();
      onLoad();
    } catch (err) {
      toast.error("Error deleting blog");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- Quill Init ---------------- */

  useEffect(() => {
    if (isModalOpen) {
      setTimeout(() => {
        if (quillRef.current && !quillInstance.current) {
          quillInstance.current = new Quill(quillRef.current, {
            theme: "snow",
            placeholder: "Write blog content...",
            modules: {
              toolbar: [
                [{ header: [1, 2, 3, false] }],
                ["bold", "italic", "underline"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["link", "image"],
                ["clean"],
              ],
            },
          });
        }

        if (selectedBlog && quillInstance.current) {
          quillInstance.current.root.innerHTML =
            selectedBlog.description || "";
        }
      }, 100);
    }

    return () => {
      if (!isModalOpen && quillInstance.current) {
        quillInstance.current = null;
      }
    };
  }, [isModalOpen]);

  /* ---------------- UI ---------------- */

  return (
    <>
      <style>
        {`
        .blog-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0 12px;
          font-family: Poppins;
        }

        .blog-table thead th {
          text-align: left;
          font-weight: 600;
          color: #6b7280;
          font-size: 13px;
          padding: 14px 18px;
        }

        .blog-row {
          background: #ffffff;
          box-shadow: 0 4px 14px rgba(0,0,0,0.06);
          transition: 0.2s ease;
        }

        .blog-row:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(0,0,0,0.08);
        }

        .blog-row td {
          padding: 18px;
          vertical-align: middle;
          font-size: 14px;
          color: #374151;
        }

        .blog-title {
          font-weight: 600;
          color: #111827;
        }

        .blog-description {
          max-width: 260px;
          color: #6b7280;
        }

        .blog-date {
          color: #9ca3af;
          font-size: 13px;
        }

        .edit-btn {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          background: #f3f4f6;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: .2s;
        }

        .edit-btn:hover {
          background: #e0e7ff;
        }

        .blog-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(17, 24, 39, 0.6);
          backdrop-filter: blur(6px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .blog-modal {
          background: #fff;
          border-radius: 16px;
          padding: 28px;
          width: 720px;
          max-width: 95%;
          box-shadow: 0 20px 60px rgba(0,0,0,0.15);
        }

        .label {
          font-weight: 600;
          margin-top: 18px;
          display: block;
        }

        .input {
          width: 100%;
          padding: 10px;
          margin-top: 8px;
          border-radius: 8px;
          border: 1px solid #ddd;
        }

        .editor {
          height: 220px;
          margin-top: 10px;
        }

        .preview-img {
          width: 100%;
          height: 180px;
          object-fit: cover;
          border-radius: 12px;
          margin-top: 10px;
          border: 1px solid #eee;
        }

        .cancel-btn {
          padding: 8px 16px;
          background: #e5e7eb;
          border: none;
          border-radius: 8px;
          cursor: pointer;
        }

        .save-btn {
          padding: 8px 16px;
          background: #4f46e5;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
        }

        .delete-btn {
          padding: 8px 16px;
          background: #ef4444;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
        }
        `}
      </style>

      <div style={{ width: "100%", overflowX: "auto" }}>
        <table className="blog-table">
          <thead>
            <tr>
              <th style={{ width: 44 }} title="Drag rows to reorder">
                {" "}
              </th>
              <th>Image</th>
              <th>Title</th>
              <th>Business</th>
              <th>Description</th>
              <th>Date</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {rowsToShow?.length > 0 ? (
              <SubmissionSortableProvider
                itemIds={sortableIds}
                disabled={reordering}
                onReorder={handleDragReorder}
              >
                {rowsToShow.map((item, index) => (
                  <SubmissionSortableTableRow
                    key={item._id || index}
                    id={item._id}
                    disabled={reordering}
                  >
                    <>
                      <td>
                        {item?.image ? (
                          <img
                            src={item.image}
                            alt="blog"
                            style={{
                              width: 60,
                              height: 60,
                              borderRadius: 10,
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          "-"
                        )}
                      </td>

                      <td className="blog-title">{item?.title}</td>
                      <td>{item?.account_id?.first_name || "-"}</td>
                      <td className="blog-description">
                        {trimText(item?.description, 30)}
                      </td>

                      <td className="blog-date">
                        {new Date(item?.createdAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </td>

                      <td>
                        <div
                          className="edit-btn"
                          onClick={() => handleOpenModal(item)}
                        >
                          <img width={16} src={editIcon} alt="edit" />
                        </div>
                      </td>
                    </>
                  </SubmissionSortableTableRow>
                ))}
              </SubmissionSortableProvider>
            ) : (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: 40 }}>
                  No blogs found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="blog-modal-overlay">
          <div className="blog-modal">
            <h2>Edit Blog</h2>

            <label className="label">Title</label>
            <input
              type="text"
              value={selectedBlog?.title || ""}
              onChange={(e) =>
                setSelectedBlog({ ...selectedBlog, title: e.target.value })
              }
              className="input"
            />

            <label className="label">Description</label>
            <div ref={quillRef} className="editor sd-submissions__quill" />

            <label className="label">Image</label>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
              <button
                type="button"
                onClick={() => setGhlGalleryOpen(true)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                  color: "white",
                  border: "none",
                  padding: "10px 18px",
                  borderRadius: 10,
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                  boxShadow: "0 2px 6px rgba(25, 118, 210, 0.35)",
                }}
              >
                <FaImages size={16} />
                {selectedBlog?.image ? "Change Image" : "Select from Media"}
              </button>
            </div>

            {selectedBlog?.image && (
              <img src={selectedBlog.image} className="preview-img" />
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 25,
              }}
            >
              <PrimaryButton
                type="button"
                className="sd-primary-btn--danger"
                onClick={() => setConfirmDelete(true)}
              >
                Delete
              </PrimaryButton>

              <div style={{ display: "flex", gap: 10 }}>
                <PrimaryButton
                  type="button"
                  className="sd-primary-btn--outline"
                  onClick={handleCloseModal}
                >
                  Cancel
                </PrimaryButton>
                <PrimaryButton type="button" onClick={handleSave} disabled={loading}>
                  {loading ? "Saving..." : "Save"}
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      )}

      <GhlMediaGallery
        open={ghlGalleryOpen}
        onClose={() => setGhlGalleryOpen(false)}
        onSelect={handleGhlImageSelect}
        title="Select Blog Image"
        enableCrop
        cropAspectRatio={1}
      />

      {confirmDelete && (
        <div className="blog-modal-overlay">
          <div
            style={{
              background: "#fff",
              padding: 24,
              borderRadius: 16,
              width: 400,
              maxWidth: "95%",
              textAlign: "center",
              boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
            }}
          >
            <h3>Confirm Delete</h3>
            <p style={{ marginTop: 10, color: "#6b7280" }}>
              Are you sure you want to delete this blog?
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 10,
                marginTop: 20,
              }}
            >
              <PrimaryButton
                type="button"
                className="sd-primary-btn--outline"
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </PrimaryButton>

              <PrimaryButton
                type="button"
                className="sd-primary-btn--danger"
                onClick={handleDelete}
                disabled={loading}
              >
                {loading ? "Deleting..." : "Yes, Delete"}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Blogs;